import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { notificationsApi } from '../lib/api';
import { Bell, Check, X, AlertTriangle, UserPlus, FileText, Briefcase, Brain, Trash2, Upload } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function NotificationBell() {
    const { t, i18n } = useTranslation();
    const [notifications, setNotifications] = useState([]);
    const [unread, setUnread] = useState(0);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const dropdownRef = useRef(null);
    const eventSourceRef = useRef(null);
    const navigate = useNavigate();

    const loadNotifications = useCallback(async () => {
        try {
            setLoading(true);
            const res = await notificationsApi.getAll(0, 20);
            setNotifications(res.data.notifications || []);
            setUnread(res.data.unread || 0);
        } catch {
            // Silent fail
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadNotifications();

        const connectSSE = () => {
            try {
                const es = new EventSource(`${API}/api/notifications/stream`, { withCredentials: true });
                es.onmessage = (event) => {
                    try {
                        const data = JSON.parse(event.data);
                        if (data && data.id) {
                            setNotifications(prev => [data, ...prev.slice(0, 19)]);
                            setUnread(prev => prev + 1);
                        }
                    } catch { /* ignore */ }
                };
                es.onerror = () => {
                    es.close();
                    setTimeout(connectSSE, 10000);
                };
                eventSourceRef.current = es;
            } catch { /* ignore */ }
        };

        connectSSE();

        return () => {
            if (eventSourceRef.current) {
                eventSourceRef.current.close();
            }
        };
    }, [loadNotifications]);

    useEffect(() => {
        const handler = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const handleMarkAllRead = async () => {
        try {
            await notificationsApi.markAllRead();
            setNotifications(prev => prev.map(n => ({ ...n, read: true })));
            setUnread(0);
        } catch { /* silent */ }
    };

    const handleNotificationClick = async (notif) => {
        if (!notif.read) {
            try {
                await notificationsApi.markRead(notif.id);
                setNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, read: true } : n));
                setUnread(prev => Math.max(0, prev - 1));
            } catch { /* silent */ }
        }
        if (notif.redirect_url) {
            setOpen(false);
            navigate(notif.redirect_url);
        }
    };

    const getIcon = (type) => {
        switch (type) {
            case 'new_user': return <UserPlus className="w-4 h-4 text-[#002FA7]" />;
            case 'quota_warning': case 'quota_warning_admin': return <AlertTriangle className="w-4 h-4 text-[#F59E0B]" />;
            case 'job_created': case 'job_updated': return <Briefcase className="w-4 h-4 text-[#059669]" />;
            case 'job_deleted': return <Trash2 className="w-4 h-4 text-[#EF4444]" />;
            case 'candidate_uploaded': return <Upload className="w-4 h-4 text-[#002FA7]" />;
            case 'candidate_analyzed': case 'batch_analysis_started': return <Brain className="w-4 h-4 text-[#7C3AED]" />;
            case 'candidate_deleted': return <Trash2 className="w-4 h-4 text-[#EF4444]" />;
            default: return <FileText className="w-4 h-4 text-[#9CA3AF]" />;
        }
    };

    const timeAgo = (dateStr) => {
        const now = new Date();
        const d = new Date(dateStr);
        const diff = Math.floor((now - d) / 1000);
        if (diff < 60) return t('notifications.time_just_now');
        if (diff < 3600) return t('notifications.time_minutes', { count: Math.floor(diff / 60) });
        if (diff < 86400) return t('notifications.time_hours', { count: Math.floor(diff / 3600) });
        return t('notifications.time_days', { count: Math.floor(diff / 86400) });
    };

    /**
     * Resolve a notification label. Prefers i18n keys (title_key / message_key)
     * sent by the backend; falls back to the raw `title` / `message` string for
     * legacy notifications that predate the i18n migration.
     */
    const localise = (notif, field) => {
        const key = notif[`${field}_key`];
        if (key && i18n.exists(key)) {
            return t(key, notif.i18n_params || {});
        }
        return notif[field] || '';
    };

    return (
        <div className="relative" ref={dropdownRef}>
            <button
                onClick={() => { setOpen(!open); if (!open) loadNotifications(); }}
                className="relative p-2 hover:bg-[#F3F4F6] rounded-lg transition-colors"
                data-testid="notification-bell"
            >
                <Bell className="w-5 h-5 text-[#4B5563]" />
                {unread > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-[#EF4444] text-white text-[10px] font-bold rounded-full flex items-center justify-center" data-testid="notification-badge">
                        {unread > 9 ? '9+' : unread}
                    </span>
                )}
            </button>

            {open && (
                <>
                    {/* Mobile backdrop (fullscreen overlay) */}
                    <div
                        className="md:hidden fixed inset-0 bg-black/40 z-40"
                        onClick={() => setOpen(false)}
                        data-testid="notification-mobile-backdrop"
                        aria-hidden="true"
                    />
                    <div
                        className="
                            fixed inset-x-0 top-16 bottom-0 md:absolute md:inset-auto md:top-full md:right-0 md:bottom-auto md:mt-2
                            md:w-96 md:rounded-lg md:max-h-[480px]
                            bg-white border border-[#E5E7EB] shadow-lg z-50 flex flex-col
                        "
                        data-testid="notification-dropdown"
                    >
                        <div className="flex items-center justify-between p-3 border-b border-[#E5E7EB]">
                            <h3 className="font-semibold text-sm text-[#0A0A0A]">{t('notifications.title')}</h3>
                            <div className="flex items-center gap-2">
                                {unread > 0 && (
                                    <button
                                        onClick={handleMarkAllRead}
                                        className="text-xs text-[#002FA7] hover:underline"
                                        data-testid="mark-all-read-btn"
                                    >
                                        {t('notifications.mark_all_read')}
                                    </button>
                                )}
                                <button
                                    onClick={() => setOpen(false)}
                                    className="p-1 hover:bg-[#F3F4F6] rounded"
                                    aria-label="Close"
                                    data-testid="notification-close-btn"
                                >
                                    <X className="w-4 h-4 text-[#9CA3AF]" />
                                </button>
                            </div>
                        </div>

                        <div className="overflow-y-auto flex-1">
                            {notifications.length === 0 ? (
                                <div className="p-6 text-center text-sm text-[#9CA3AF]">
                                    {t('notifications.empty')}
                                </div>
                            ) : (
                                notifications.map((notif) => (
                                    <div
                                        key={notif.id}
                                        className={`flex items-start gap-3 p-3 border-b border-[#E5E7EB] last:border-0 hover:bg-[#F9FAFB] cursor-pointer transition-colors ${!notif.read ? 'bg-[#EFF6FF]' : ''}`}
                                        onClick={() => handleNotificationClick(notif)}
                                        data-testid={`notification-item-${notif.id}`}
                                    >
                                        <div className="mt-0.5 flex-shrink-0">
                                            {getIcon(notif.type)}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className={`text-sm break-words ${!notif.read ? 'font-semibold text-[#0A0A0A]' : 'text-[#4B5563]'}`}>
                                                {localise(notif, 'title')}
                                            </p>
                                            <p className="text-xs text-[#4B5563] mt-0.5 break-words whitespace-pre-line">{localise(notif, 'message')}</p>
                                            <p className="text-[10px] text-[#9CA3AF] mt-1">{timeAgo(notif.created_at)}</p>
                                        </div>
                                        {!notif.read && (
                                            <div className="w-2 h-2 bg-[#002FA7] rounded-full flex-shrink-0 mt-2" />
                                        )}
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
